import { Badge, Box, HStack, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { FcGoogle } from "react-icons/fc";
import { LuBriefcase, LuChevronRight } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { GoogleAdsCustomer, getGoogleAdsCustomers } from "../api/google-ads";
import { useBreadcrumbs } from "../components/breadcrumbs";
import { Button } from "../components/button";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import Page from "../components/page";
import PageBody from "../components/page-body";
import PageHeader from "../components/page-header";
import { useAppDispatch, useAppSelector } from "../hooks";
import { googleAdsAccountPath } from "../hooks/google-ads-login-customer-id";
import { useLoading } from "../hooks/loading";
import { useToaster } from "../hooks/toaster";
import { useWorkspaceURL } from "../hooks/workspace-url";
import {
  MOCK_GOOGLE_ADS_ACCOUNT,
  MOCK_GOOGLE_ADS_ACCOUNT_NAME,
  isMockEnabled,
} from "../lib/mock";
import { loadIntegrations } from "../store/workspaceSlice";
import { IntegrationStatus } from "../types/integration";

// The customer endpoint is the one that isn't mocked, so this stands in
// for the list rather than calling out to it.
const MOCK_CUSTOMERS: GoogleAdsCustomer[] = [
  {
    id: MOCK_GOOGLE_ADS_ACCOUNT,
    name: MOCK_GOOGLE_ADS_ACCOUNT_NAME,
    "login-customer-id": null,
    "manager?": false,
  },
];

const accountDetail = (
  customer: GoogleAdsCustomer,
  managerName: string | null,
) => {
  // Google's descriptive names are optional and often absent, in which
  // case the name is already the id.
  const id = customer.name === customer.id ? null : customer.id;
  return [
    id,
    managerName && `Managed by ${managerName}`,
    customer["manager?"] && "No campaigns of its own",
  ]
    .filter(Boolean)
    .join(" · ");
};

export default function GoogleAdsAccountsPage() {
  const workspaceSlug = useAppSelector((state) => state.workspace.slug);
  const integrations = useAppSelector((state) => state.workspace.integrations);
  const workspaceURL = useWorkspaceURL();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const withToaster = useToaster();

  const [fetched, setFetched] = React.useState<GoogleAdsCustomer[]>([]);
  const [isLoading, withLoading] = useLoading();

  // Mock mode stands in for the whole integration, connection included:
  // demoing without one is the point of it.
  const isConnected =
    isMockEnabled() ||
    integrations.find((i) => i.id === "google-ads")?.status ===
      IntegrationStatus.connected;

  // A constant rather than something to go and get, so it stands in at
  // render instead of being loaded into state.
  const customers = isMockEnabled() ? MOCK_CUSTOMERS : fetched;

  useBreadcrumbs([
    { label: "Dashboard", to: workspaceURL("") },
    { label: "Google" },
  ]);

  // The store starts empty, so a direct load of this URL would otherwise
  // read as disconnected.
  React.useEffect(() => {
    dispatch(loadIntegrations());
  }, [dispatch]);

  const loadCustomers = React.useCallback(async () => {
    if (!workspaceSlug || !isConnected || isMockEnabled()) return;
    await withLoading(async () => {
      await withToaster(async () => {
        setFetched(await getGoogleAdsCustomers(workspaceSlug));
      });
    });
  }, [isConnected, withLoading, withToaster, workspaceSlug]);

  React.useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  if (!isConnected) {
    return (
      <Page>
        <Stack flex="1">
          <PageHeader title="Google" />
          <PageBody>
            <HStack flex="1" alignItems="center">
              <EmptyState
                icon={
                  <IconCircle Icon={LuBriefcase} size="md" variant="flat" />
                }
                title="Google Ads Not Connected"
                description="Connect Google Ads to start managing accounts."
              >
                <Button
                  onClick={() =>
                    navigate(workspaceURL("settings/integrations"))
                  }
                >
                  Go to Integration Settings
                </Button>
              </EmptyState>
            </HStack>
          </PageBody>
        </Stack>
      </Page>
    );
  }

  return (
    <Page loading={isLoading}>
      <Stack flex="1" gap="6">
        <PageHeader
          title="Google"
          subtitle="Ad accounts the connected Google Ads profile can reach"
        />
        {customers.length === 0 ? (
          <PageBody>
            <HStack flex="1" alignItems="center">
              <EmptyState
                icon={
                  <IconCircle Icon={LuBriefcase} size="md" variant="flat" />
                }
                title="No Accounts"
                description="No Google Ads accounts found for this profile."
              />
            </HStack>
          </PageBody>
        ) : (
          <Box
            background="white"
            // A real border, not shadow="card"'s inset ring: rows sit inside a
            // border but paint over an inset one once they take a hover background.
            borderColor="border"
            borderRadius="l3"
            borderWidth="1"
            boxShadow="xs"
            maxWidth={"760px" as any}
            overflow="hidden"
            width="full"
          >
            {customers.map((customer, index) => {
              const managerId = customer["login-customer-id"];
              const managerName =
                customers.find((c) => c.id === managerId)?.name ?? managerId;
              const detail = accountDetail(customer, managerName);
              // A manager holds no campaigns of its own, so its row leads
              // nowhere. It stays listed because a conversion action can
              // live on one.
              const campaignsPath = customer["manager?"]
                ? null
                : googleAdsAccountPath(
                    workspaceURL(
                      `google-ads-accounts/${customer.id}/campaigns`,
                    ),
                    managerId,
                  );
              return (
                <HStack
                  key={customer.id}
                  borderColor="border.soft"
                  borderTopWidth={index > 0 ? "1" : "0"}
                  cursor={campaignsPath ? ("pointer" as any) : undefined}
                  gap="4"
                  onClick={
                    campaignsPath ? () => navigate(campaignsPath) : undefined
                  }
                  paddingX="6"
                  paddingY="4"
                  _hover={
                    campaignsPath ? { background: "bg.subtle" } : undefined
                  }
                >
                  <HStack
                    background="white"
                    borderRadius="l2"
                    boxShadow="insetRing"
                    flexShrink="0"
                    height="8"
                    justify="center"
                    width="8"
                  >
                    <FcGoogle size="16" />
                  </HStack>
                  <Stack flex="1" gap="0" minWidth="0">
                    <HStack gap="2" minWidth="0">
                      <Text fontSize="sm" fontWeight="medium" truncate>
                        {customer.name}
                      </Text>
                      {customer["manager?"] && (
                        <Badge flexShrink="0" size="sm" variant="subtle">
                          Manager
                        </Badge>
                      )}
                    </HStack>
                    {detail && (
                      <Text color="fg.muted" fontSize="xs" truncate>
                        {detail}
                      </Text>
                    )}
                  </Stack>
                  {campaignsPath && (
                    <Box color="fg.muted" flexShrink="0">
                      <LuChevronRight size="16" />
                    </Box>
                  )}
                </HStack>
              );
            })}
          </Box>
        )}
      </Stack>
    </Page>
  );
}
